import { useTranslation } from 'react-i18next';
import useOpenState from '../../../../hooks/useOpenState';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import * as _ from 'lodash';
import { Box, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import moment from 'moment';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { getTableScrollbar } from '../../../../util/scrollbar';
import DeleteIcon from '@mui/icons-material/Delete';
import Notes from '../../../../util/notes';
import MessageBox from '../../../../util/messageBox';
import Grid from '@mui/material/Grid';
import InformationBox from '../../../../components/other/informationBox';
import { useTheme } from '@mui/material/styles';
import { openPatientForm } from '../../../../store/reducers/patientContext';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        overflowX: 'auto',
        padding: 10,
        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#222425',
    },
    tableHeadRow: {
        display: 'flex',
    },
    headerCell: {
        width: 135,
        marginLeft: 10,
        padding: 10,
        backgroundColor: theme.palette.mode === 'light' ? '#808080' : '#3D3E40',
        borderRadius: 10,
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        fontWeight: 500,
        borderBottom: 'none',
    },
    apsLabel: {
        fontWeight: 500,
        fontSize: '14px',
        color: '#fff',
    },
    headerCellDate: {
        fontWeight: 500,
        fontSize: '11px',
        color: '#fff',
    },
    deleteButton: {
        padding: 0,
        width: 20,
        height: 20,
        color: '#fff',
        '& svg': {
            fontSize: 20,
        },
    },
    emptyHeadCell: {
        width: 132,
        marginRight: 10,
        padding: 8,
        borderBottom: 'none',
    },
    labelColumn: {
        width: 135,
        padding: 8,
        borderBottom: 'none',
    },
    labelRow: {
        width: 135,
        height: 28,
        marginBottom: 10,
        padding: '0 6px',
        backgroundColor: theme.palette.mode === 'light' ? '#808080' : '#313233',
        color: 'white',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
    },
    labelText: {
        fontSize: '11px',
        fontWeight: 700,
        padding: '0px 6px 0px 4px',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        maxWidth: '100%',
    },
    dataColumn: {
        width: 135,
        marginLeft: 10,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        borderBottom: 'none',
    },
    dataTile: {
        width: 135,
        height: 28,
        backgroundColor: theme.palette.mode === 'light' ? '#808080' : '#3D3E40',
        color: 'white',
        borderRadius: 6,
        padding: '10px 16px',
        marginBottom: 10,
        display: 'flex',
        alignItems: 'center',
        fontWeight: 500,
    },
    dataText: {
        fontSize: '11px',
        fontWeight: 400,
    },

    cancelledCell: {
        textDecoration: 'line-through',
        opacity: 0.6,
    },
}));

export const getCellValue = (label, value) => {
    if (value === undefined || value === null || value === '') return '-';

    const num = Number(value);

    if (label === 'total') {
        if (num <= 2) return `${num} (No pain)`;
        if (num <= 7) return `${num} (Mild)`;
        if (num <= 13) return `${num} (Moderate)`;
        if (num > 13) return `${num} (Severe)`;
    }

    if (label === 'typeOfPain' || label === 'initials1') {
        return value;
    }

    const painMap = {
        0: 'None',
        1: 'Mild',
        2: 'Moderate',
        3: 'Severe',
    };

    return painMap[num] || '-';
};

export const AbbeyPainScaleFormObs = (props) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const classes = useStyles();
    const { t } = useTranslation();

    const [abbeyPainScaleData, setAbbeyPainScaleData] = useState(props.patient?.studies?.abbeyPainScaleForm || []);

    const [keysList, setKeysList] = useState([]);
    const [idToDelete, setIdToDelete] = useState(null);
    const [isOpenDeleteEntry, openDeleteEntry, closeDeleteEntry] = useOpenState(false, true);

    const scrollRef = useRef(null);

    useEffect(() => {
        if (props.onCustomButtons) {
            props.onCustomButtons([
                <AgyleButton key="add" onClick={() => dispatch(openPatientForm('abbeyPainScaleForm'))} colour="primary">
                    {t('Add New Observation')}
                </AgyleButton>,
            ]);
        }
        if (props.onHideSubmit) {
            props.onHideSubmit(true);
        }
    }, []);

    useEffect(() => {
        if (props.onDisableSubmit) {
            props.onDisableSubmit(props.disableSave);
        }
    }, [props.disableSave]);

    useEffect(() => {
        setAbbeyPainScaleData(props.patient?.studies?.abbeyPainScaleForm || []);
    }, [props.patient?.studies?.abbeyPainScaleForm]);

    useEffect(() => {
        if (abbeyPainScaleData.length > 0 && !keysList.length) {
            const keys = Object.keys(abbeyPainScaleData[0]);
            setKeysList(keys);
        }
    }, [abbeyPainScaleData, keysList]);

    const handleDeleteEntry = () => {
        const cancelledEntry = abbeyPainScaleData.find((item) => item.id === idToDelete);

        const newState = abbeyPainScaleData.map((item) => (item.id === idToDelete ? { ...item, cancelled: true } : item));
        setAbbeyPainScaleData(newState);

        const data = [
            {
                name: 'studies.abbeyPainScaleForm',
                value: newState,
                noteToAdd: Notes.getAbbeyPainScaleNote(cancelledEntry, true),
            },
        ];

        props.onChange(data);

        closeDeleteEntry();
        setIdToDelete(null);
    };

    const getDataBgColor = (string) => {
        if (string.includes('Moderate')) {
            return '#F7A202';
        } else if (string.includes('Severe')) {
            return '#E25B61';
        } else if (!string.includes('Moderate') && !string.includes('Moderate') && theme.palette.mode === 'light') {
            return '#808080';
        } else if (!string.includes('Moderate') && !string.includes('Moderate') && theme.palette.mode !== 'light') {
            return '#3D3E40';
        }
    };

    const renderObsTable = () => {
        const labels = [
            'APS Score',
            'Vocalisation',
            'Facial expression',
            'Change in body language',
            'Behavioural',
            'Physiological',
            'Physical',
            'Type of pain',
            'By',
        ];
        const abbeyField = [
            'total',
            'vocalisation',
            'facialExpression',
            'changeInBody',
            'behavioural',
            'physiological',
            'physical',
            'typeOfPain',
            'initials1',
        ];

        return (
            <TableContainer className={clsx(getTableScrollbar(), classes.tableContainer)}>
                <Table ref={scrollRef}>
                    <TableHead>
                        <TableRow className={classes.tableHeadRow}>
                            <TableCell className={classes.emptyHeadCell}></TableCell>
                            {abbeyPainScaleData.map((property, index) => (
                                <TableCell key={property.id} className={classes.headerCell}>
                                    <Stack spacing={1}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography
                                                variant="body2"
                                                className={clsx(classes.apsLabel, { [classes.cancelledCell]: property.cancelled })}
                                            >
                                                APS {index + 1}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                className={clsx(classes.deleteButton, { [classes.cancelledCell]: property.cancelled })}
                                                onClick={() => {
                                                    setIdToDelete(property.id);
                                                    openDeleteEntry();
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {property.dateAndTime ? (
                                            <>
                                                <Typography
                                                    variant="body1"
                                                    className={clsx(classes.headerCellDate, {
                                                        [classes.cancelledCell]: property.cancelled,
                                                    })}
                                                >
                                                    {moment(property.dateAndTime).format('DD/MM/YYYY') +
                                                        ' ' +
                                                        moment(property.dateAndTime).format('HH:mm')}
                                                </Typography>
                                            </>
                                        ) : (
                                            <Typography variant="body1">-</Typography>
                                        )}
                                    </Stack>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow className={classes.tableHeadRow}>
                            <TableCell className={classes.labelColumn}>
                                {labels.map((label) => (
                                    <div key={label} className={classes.labelRow}>
                                        <Typography className={classes.labelText}>{t ? t(label) : label}</Typography>
                                    </div>
                                ))}
                            </TableCell>
                            {abbeyPainScaleData.map((property) => (
                                <TableCell key={property.id} className={classes.dataColumn}>
                                    {abbeyField.map((label) => {
                                        const value = getCellValue(label, property[label]);
                                        const bgColor = getDataBgColor(value);
                                        const textColor = bgColor === '#808080' || bgColor === '#3D3E40' ? 'white' : 'black';

                                        return (
                                            <div
                                                key={label}
                                                className={clsx(classes.dataTile, { [classes.cancelledCell]: property.cancelled })}
                                                style={{ backgroundColor: bgColor, color: textColor }}
                                            >
                                                <Typography
                                                    className={clsx(classes.dataText, { [classes.cancelledCell]: property.cancelled })}
                                                >
                                                    {value}
                                                </Typography>
                                            </div>
                                        );
                                    })}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const renderNoObsInfo = () => {
        return (
            <Grid item xs={12} sx={{ px: 2, mt: 2, mb: 4 }}>
                <InformationBox type={'info'}>{t('Observations not made.')}</InformationBox>
            </Grid>
        );
    };

    return (
        <>
            {_.isEmpty(abbeyPainScaleData) && renderNoObsInfo()}
            {!_.isEmpty(abbeyPainScaleData) && renderObsTable()}
            <MessageBox
                open={isOpenDeleteEntry}
                onClose={closeDeleteEntry}
                title={t('Delete Observation')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleDeleteEntry}
                message={t('Are you sure you want to delete this observation?')}
                warning
            />
        </>
    );
};

export default AbbeyPainScaleFormObs;
